/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import type { FC } from 'react';
import type ModalIssueAppType from '@jira/issue__issue-view/src/IssueModal.tsx';
import type IssuePageType from '@jira/spa-apps__issue/src/SpaAppsIssue.tsx';
import {
	createLayout,
	type Layout,
} from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { markAsBentoBundleReady } from '@jira/platform__spa/src/services/mark-bento-bundle-as-ready/index.tsx';
import { lazyAfterPaint, lazyForPaint } from '@atlassian/react-loosely-lazy';
import { createRouterSelector, matchQuery } from '@atlassian/react-resource-router';
import { setServerEvaluatedIssuePreload } from './preloadIdentifier.tsx';

type ViewModeType = 'MODAL' | 'SIDEBAR' | 'DEFAULT';

type PreloadContract = {
	query?: string[];
	params?: {
		key: string;
		value?: string;
	}[];
}[];

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyIssuePage = lazyAfterPaint<typeof IssuePageType>(
	() =>
		markAsBentoBundleReady(
			import(
				/* webpackChunkName: "async-issue-app--full-page", jiraSpaEntry: "spa-issue" */ '@jira/spa-apps__issue/src/IssueViewWithRedirect.tsx'
			),
		),
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const AsyncIssueAppComponent = lazyAfterPaint(
	() =>
		markAsBentoBundleReady(
			import(
				/* webpackChunkName: "async-issue-app" */ '@jira/issue__issue-view/src/IssueAppEntrypoint.tsx'
			),
		).then((module) => module.IssueAppWithCriticalQueryPreloader),
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const IssueModal = lazyForPaint<typeof ModalIssueAppType>(
	() =>
		import(
			/* webpackChunkName: "async-modal-issue-app" */ '@jira/issue__issue-view/src/IssueModal.tsx'
		),
	{ ssr: false },
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const IssueModalContents = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-modal-issue-app--issue-view" */ '@jira/issue__issue-view/src/IssueViewWithHooks.tsx'
		),
	{ ssr: false },
);

const imports = {
	fullPage: () => LazyIssuePage.preload(),
	embedded: () => AsyncIssueAppComponent.preload(),
	pageLoad: (viewModeToPreload: ViewModeType) => {
		// issue view that opens as a modal
		if (viewModeToPreload === 'MODAL' || viewModeToPreload === 'DEFAULT') {
			IssueModal.preload();
			IssueModalContents.preload();
		}
		// issue view that opens as a side panel
		if (viewModeToPreload === 'SIDEBAR') {
			AsyncIssueAppComponent.preload();
		}
	},
} as const;

const PrefetchCBBResourcesComponent = lazyAfterPaint(
	() =>
		import(
			/* webpackChunkName: "async-classic-resources" */ '@jira/router-resources__classic-projects/src/GetCBBResources.tsx'
		),
	{ entryFsSsrLiftImportToModule: true },
);

// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
if (!__SERVER__ && window.SPA_STATE?.serverIssuePreload) {
	// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly fullPage: () => Promise<typeof import("/buildeng/bamboo-agent-home/xml-data/build-dir/JF-TSMIG123-APPLY/src/packages/spa-apps/issue/src/index")>; readonly embedded: () => Promise<...>; }'.

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const importSupplier = imports[window.SPA_STATE.serverIssuePreload];

	importSupplier && importSupplier();
}

const ISSUE_PRELOAD_COMPONENT_MARK = 'IssuePreloadComponent:rendered';
const useParamsSelector = createRouterSelector((state) => ({
	query: state.query,
	params: state.match.params,
}));

const IssuePreloadComponentBuilder =
	(identifier: string, preloadSupplier: () => unknown, preloadContract?: PreloadContract): FC =>
	() => {
		// oxlint-disable-next-line react-hooks/rules-of-hooks
		const { query, params } = useParamsSelector();

		if (
			// If we have an undefined contract, assume we want to preload
			!preloadContract ||
			// Preload if we match the contract
			preloadContract.some(
				(preloadingMatch) =>
					// Match query with router function
					// @ts-expect-error - TS2554 - Expected 3 arguments, but got 2.
					(!preloadingMatch.query || matchQuery(preloadingMatch.query, query)) &&
					// Match path params with simple object structure, where if value is undefined
					// we assume we match all values
					(!preloadingMatch.params ||
						preloadingMatch.params.every(
							({ key, value }) =>
								params &&
								params[key] !== undefined &&
								(value === undefined || params[key] === value),
						)),
			)
		) {
			if (!__SERVER__) {
				preloadSupplier();
			}

			if (__SERVER__) {
				setServerEvaluatedIssuePreload(identifier);
			} else {
				performance.mark(ISSUE_PRELOAD_COMPONENT_MARK);
			}
		}

		return null;
	};

const FullPageIssuePreloadComponent = IssuePreloadComponentBuilder('fullPage', imports.fullPage);
FullPageIssuePreloadComponent.displayName = 'FullPageIssuePreloadComponent';

export const fullPageIssuePreloadLayout: Layout = createLayout({
	globalComponents: [FullPageIssuePreloadComponent, PrefetchCBBResourcesComponent],
});

export const embeddedIssuePreloadLayoutBuilder = (preloadContract?: PreloadContract): Layout => {
	const EmbeddedIssuePreloadComponent = IssuePreloadComponentBuilder(
		'embedded',
		imports.embedded,
		preloadContract,
	);
	EmbeddedIssuePreloadComponent.displayName = 'EmbeddedIssuePreloadComponent';
	return createLayout({
		globalComponents: [EmbeddedIssuePreloadComponent],
	});
};

export const pageLoadIssuePreloadLayoutBuilder = (
	preloadContract?: PreloadContract,
	viewModeToPreload?: ViewModeType,
): Layout => {
	const PageLoadIssuePreloadComponent = IssuePreloadComponentBuilder(
		'pageLoad',
		() => imports.pageLoad(viewModeToPreload || 'DEFAULT'),
		preloadContract,
	);
	PageLoadIssuePreloadComponent.displayName = 'PageLoadIssuePreloadComponent';
	return createLayout({
		globalComponents: [PageLoadIssuePreloadComponent],
	});
};
